/**
 * Runtime tenant resolution.
 *
 * In dev mode, __TENANT_ID__ is injected by Vite (from VITE_TENANT env var)
 * so the dev server works on localhost without a real tenant domain.
 *
 * In production, __TENANT_ID__ is null and tenant is resolved from the
 * browser hostname (each tenant has its own domain on Cloudflare Pages).
 */
import {
  TENANT_CONFIGS,
  resolveTenantConfigByHost,
  resolveTenantConfigByHostFromConfigs,
  type TenantConfig,
  type TenantConfigs,
} from "@waypoint/shared";

declare const __TENANT_ID__: string | null;

const getRuntimeTenantConfigs = (): TenantConfigs => {
  if (typeof window === "undefined") {
    return TENANT_CONFIGS;
  }
  return window.__WAYPOINT_TENANT_CONFIG__
    ? [window.__WAYPOINT_TENANT_CONFIG__]
    : TENANT_CONFIGS;
};

const findTenantById = (
  tenantId: string,
  configs: TenantConfigs
): TenantConfig | undefined =>
  configs.find(
    (c: TenantConfig) => c.id.toLowerCase() === tenantId.toLowerCase()
  );

export const resolvedTenant: TenantConfig = (() => {
  const tenantId = typeof __TENANT_ID__ !== "undefined" ? __TENANT_ID__ : null;
  const runtimeConfigs = getRuntimeTenantConfigs();

  if (!import.meta.env.DEV) {
    const hostMatchedTenant = resolveTenantConfigByHostFromConfigs(
      window.location.hostname,
      runtimeConfigs
    );
    if (hostMatchedTenant) {
      return hostMatchedTenant;
    }

    const availableTenants = runtimeConfigs
      .map((config) => `${config.id} [${config.domains.join(", ")}]`)
      .join("; ");
    throw new Error(
      `[Tenant] No tenant config matches production host "${window.location.hostname}". ` +
        `Available runtime configs: ${availableTenants || "none"}`
    );
  }

  if (tenantId) {
    const found = findTenantById(tenantId, runtimeConfigs) ??
      findTenantById(tenantId, TENANT_CONFIGS);
    if (found) return found;
    console.warn(
      `[Tenant] "${tenantId}" not found, falling back to hostname resolution`
    );
  }
  return resolveTenantConfigByHost(window.location.hostname);
})();

export const resolvedTenantId: string = resolvedTenant.id;
